import { useElementWidth } from "@/hooks/useElementWidth";
import { usePainEntries } from "@/hooks/usePainEntries";
import {
  formatMeasurementValue,
  trendWindow,
  trendWindowRange,
} from "@/lib/body-measurements";
import { isoWeekOf } from "@/lib/days";
import { aggregatePainWeekly } from "@/lib/pain";
import type { PainTrendConfig } from "@/lib/schema";
import {
  type AxisScale,
  axisGutter,
  axisTickLabel,
  chartLayout,
  gridlinePositions,
  isLabelled,
  labelStride,
  seriesGeometry,
  VALUE_LABEL_WIDTH,
  valueLabelPositions,
  WEEK_LABEL_WIDTH,
  xPosition,
} from "@/widgets/body-measurement-trend/chart";
import type { WidgetWeekViewProps } from "@/widgets/types";

type Props = WidgetWeekViewProps<PainTrendConfig>;

// The whole Smertenivå scale, always. An auto-fitted axis would stretch a
// week moving from 3 to 4 across the full chart height and make a small
// change look dramatic; on the fixed scale it looks as small as it is.
const PAIN_SCALE: AxisScale = {
  minimum: 1,
  maximum: 10,
  step: 3,
  ticks: [1, 4, 7, 10],
  decimals: 0,
};

const SERIES_COLOR = "var(--chart-series-pain)";

/** Same caps as Måltrend's spread whiskers, for the same reason. */
const WHISKER_CAP_WIDTH = 10;

export function WeekView({ config, week }: Props) {
  // Anchored to the viewed week; the settings preview (no week) ends the
  // window at the current ISO week.
  const anchor = week
    ? { year: week.year, weekNumber: week.weekNumber }
    : isoWeekOf(new Date());
  const weeks = trendWindow(anchor.year, anchor.weekNumber);
  const range = trendWindowRange(anchor.year, anchor.weekNumber);
  const { entries, loading } = usePainEntries(range.startDate, range.endDate);

  const aggregates = aggregatePainWeekly(entries, weeks);
  const hasAnyData = aggregates.some((a) => a !== null);

  const [measuredWidth, measureChart] = useElementWidth();
  const layout = chartLayout(measuredWidth, axisGutter(PAIN_SCALE, ""));
  const geometry = seriesGeometry(aggregates, PAIN_SCALE, layout);
  const gridlines = gridlinePositions(PAIN_SCALE, layout);
  const labels = valueLabelPositions(geometry.points, layout);
  const weekStride = labelStride(weeks.length, layout, WEEK_LABEL_WIDTH);
  const valueStride = labelStride(weeks.length, layout, VALUE_LABEL_WIDTH);

  return (
    <section
      className="widget-card pain-trend"
      data-testid={`widget-week-${config.id}`}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
      </header>
      <div className="widget-card-body">
        {loading ? (
          <p className="hint">Laster…</p>
        ) : !hasAnyData ? (
          <p className="hint" data-testid={`pain-trend-empty-${config.id}`}>
            Ingen smertenivå i de siste {weeks.length} ukene enda.
          </p>
        ) : (
          <div className="trend-chart-wrap" ref={measureChart}>
            {measuredWidth > 0 ? (
              <svg
                className="trend-chart"
                width={layout.width}
                height={layout.height}
                viewBox={`0 0 ${layout.width} ${layout.height}`}
                role="img"
                aria-label={`${config.label}, snitt per uke på en skala fra 1 til 10`}
                data-testid={`pain-trend-chart-${config.id}`}
              >
                {gridlines.slice(1).map((y) => (
                  <line
                    key={y}
                    className="trend-gridline"
                    x1={layout.plotLeft}
                    y1={y}
                    x2={layout.plotRight}
                    y2={y}
                  />
                ))}
                <line
                  className="trend-frame"
                  x1={layout.plotLeft}
                  y1={layout.plotBottom}
                  x2={layout.plotRight}
                  y2={layout.plotBottom}
                />
                <g className="trend-axis">
                  {PAIN_SCALE.ticks.map((tick, i) => (
                    <text
                      key={tick}
                      className="trend-axis-label"
                      x={layout.plotLeft - 6}
                      y={gridlines[i] + 3.5}
                      textAnchor="end"
                    >
                      {axisTickLabel(tick, PAIN_SCALE.decimals, "")}
                    </text>
                  ))}
                </g>
                {weeks.map((w, i) =>
                  isLabelled(i, weeks.length, weekStride) ? (
                    <text
                      key={`${w.year}-${w.weekNumber}`}
                      className="trend-x-label"
                      x={xPosition(i, weeks.length, layout)}
                      y={layout.plotBottom + 16}
                      textAnchor="middle"
                    >
                      Uke {w.weekNumber}
                    </text>
                  ) : null,
                )}
                {geometry.linePaths.map((d) => (
                  <path
                    key={d}
                    d={d}
                    fill="none"
                    stroke={SERIES_COLOR}
                    strokeWidth={2}
                  />
                ))}
                {geometry.points.map((p) => {
                  const w = weeks[p.index];
                  return (
                    <circle
                      key={p.index}
                      cx={p.x}
                      cy={p.y}
                      r={3}
                      fill={SERIES_COLOR}
                      data-testid={`pain-trend-point-${w.year}-${w.weekNumber}`}
                    />
                  );
                })}
                {geometry.points
                  .filter((p) => p.hasSpread)
                  .map((p) => {
                    const w = weeks[p.index];
                    const half = WHISKER_CAP_WIDTH / 2;
                    return (
                      <g
                        key={p.index}
                        className="trend-spread"
                        stroke={SERIES_COLOR}
                        data-testid={`pain-trend-spread-${w.year}-${w.weekNumber}`}
                      >
                        <line x1={p.x} y1={p.top} x2={p.x} y2={p.bottom} />
                        <line
                          x1={p.x - half}
                          y1={p.top}
                          x2={p.x + half}
                          y2={p.top}
                        />
                        <line
                          x1={p.x - half}
                          y1={p.bottom}
                          x2={p.x + half}
                          y2={p.bottom}
                        />
                      </g>
                    );
                  })}
                {geometry.points.map((p) => {
                  const w = weeks[p.index];
                  const y = labels.get(p.index);
                  if (y === undefined) return null;
                  if (!isLabelled(p.index, weeks.length, valueStride)) {
                    return null;
                  }
                  return (
                    <text
                      key={p.index}
                      className="trend-value-label"
                      x={p.x}
                      y={y}
                      textAnchor="middle"
                      data-testid={`pain-trend-value-${w.year}-${w.weekNumber}`}
                    >
                      {formatMeasurementValue(p.value)}
                    </text>
                  );
                })}
              </svg>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
